/**
 * Company Logo Service
 *
 * Fetches company logos from domain names (extracted from email or website).
 * Uses CompanyEnrich API (primary) with Google Favicon API as fallback.
 */

const PERSONAL_EMAIL_DOMAINS = new Set([
  // International
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.it', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de',
  'hotmail.com', 'hotmail.it', 'hotmail.co.uk', 'hotmail.fr',
  'outlook.com', 'outlook.it',
  'live.com', 'live.it',
  'msn.com', 'aol.com',
  'icloud.com', 'me.com', 'mac.com',
  'protonmail.com', 'proton.me', 'protonmail.ch',
  'tutanota.com', 'tutamail.com',
  'mail.com', 'email.com',
  'yandex.com', 'yandex.ru',
  'zoho.com',
  // Italian ISPs
  'libero.it', 'virgilio.it', 'tiscali.it', 'alice.it',
  'tin.it', 'fastwebnet.it', 'aruba.it',
  'tim.it', 'vodafone.it', 'wind.it', 'tre.it',
  // Italian PEC (certified email)
  'pec.it', 'legalmail.it', 'postecert.it', 'arubapec.it',
  'pec.libero.it', 'pecveneto.it', 'sicurezzapostale.it',
]);

/**
 * Extract domain from email address.
 * Returns null for personal/ISP email domains.
 */
export function extractDomainFromEmail(email: string): string | null {
  if (!email || !email.includes('@')) return null;
  const domain = email.split('@')[1]?.toLowerCase().trim();
  if (!domain) return null;
  if (PERSONAL_EMAIL_DOMAINS.has(domain)) return null;
  return domain;
}

/**
 * Extract domain from website URL.
 * Handles URLs with or without protocol prefix.
 */
export function extractDomainFromWebsite(website: string): string | null {
  if (!website) return null;
  try {
    const normalized = website.match(/^https?:\/\//) ? website : `https://${website}`;
    const url = new URL(normalized);
    return url.hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Get the best domain from a client's data.
 * Priority: website > email (website is more reliable for company logos).
 */
export function getClientDomain(email?: string, website?: string): string | null {
  const websiteDomain = extractDomainFromWebsite(website || '');
  if (websiteDomain) return websiteDomain;
  return extractDomainFromEmail(email || '');
}

/** Indirizzo del logo CompanyEnrich per un dominio (nessuna chiamata di rete). */
export function companyLogoUrlForDomain(domain: string): string {
  return `https://api.companyenrich.com/logo/${domain}`;
}

/**
 * Logo da mostrare in elenco per un'anagrafica (segnalazione 9447b2cd).
 * Priorità al logo salvato; se manca (es. clienti importati o convertiti, mai
 * passati dal recupero automatico alla creazione) si prova il logo dal dominio
 * di sito o email aziendale. Le persone fisiche senza logo salvato restano con
 * le iniziali: un'email aziendale non è il loro logo.
 */
export function resolveDisplayLogoUrl(client: {
  logo_url?: string | null;
  email?: string | null;
  website?: string | null;
  entity_type?: string | null;
}): string | null {
  if (client.logo_url) return client.logo_url;
  if (client.entity_type === 'individual') return null;
  const domain = getClientDomain(client.email || undefined, client.website || undefined);
  return domain ? companyLogoUrlForDomain(domain) : null;
}

/**
 * Validate that an image URL actually loads a valid image.
 */
function validateImageUrl(url: string, timeoutMs = 3000): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      resolve(false);
    }, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      resolve(img.naturalWidth >= 16 && img.naturalHeight >= 16);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    img.src = url;
  });
}

/**
 * Fetch the best available logo URL for a domain.
 *
 * Strategy:
 * 1. Try CompanyEnrich API (high quality PNG logos, free, no auth)
 * 2. Fallback to Google Favicon API (always available, lower quality)
 *
 * Returns the logo URL or null if nothing works.
 */
export async function fetchBestLogoUrl(domain: string): Promise<string | null> {
  if (!domain) return null;

  // Primary: CompanyEnrich API (high quality company logos)
  const companyEnrichUrl = companyLogoUrlForDomain(domain);
  try {
    const isValid = await validateImageUrl(companyEnrichUrl, 4000);
    if (isValid) return companyEnrichUrl;
  } catch {
    // CompanyEnrich failed, try fallback
  }

  // Fallback: Google Favicon API (always works, but only favicons)
  const googleFaviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  try {
    const isValid = await validateImageUrl(googleFaviconUrl, 3000);
    if (isValid) return googleFaviconUrl;
  } catch {
    // Google also failed
  }

  return null;
}
